import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/Sections";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ShieldCheck, ExternalLink } from "lucide-react";

const fmtNum = (n, d = 0) => n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });

export default function AsteroidTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await api.get("/neo", { params: { start_date: d, end_date: d } });
      setData(r.data);
    } catch (e) {
      console.error("NEO load failed:", e);
    }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line
  useEffect(() => { load(date); }, [date]);

  return (
    <>
      <PageHero
        eyebrow="Asteroid Tracker"
        title="Rocks passing"
        accent="through the night."
        subtitle="Near-Earth objects making close approaches today. Size, speed, miss distance, and hazard classification, observed by our planetary defense feed."
      >
        <div className="flex items-center gap-3">
          <label className="btn-pill btn-secondary cursor-pointer" data-testid="neo-date-picker">
            <span className="text-white/70 text-xs uppercase tracking-widest">Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent border-0 outline-none text-sm text-white"
            />
          </label>
          <button onClick={() => setDate(new Date().toISOString().slice(0, 10))} className="btn-pill btn-ghost">Today</button>
        </div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        {data && !loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10 stagger">
            <Stat label="Total tracked" value={data.count} />
            <Stat label="Hazardous" value={(data.asteroids || []).filter(a => a.hazardous).length} accent="hazard" />
            <Stat label="Closest (km)" value={fmtNum(Math.min(...(data.asteroids || []).map(a => Number(a.miss_distance_km) || Infinity)))} />
            <Stat label="Farthest (km)" value={fmtNum(Math.max(...(data.asteroids || []).map(a => Number(a.miss_distance_km) || 0)))} />
            <Stat label="Fastest (km/h)" value={fmtNum(Math.max(...(data.asteroids || []).map(a => Number(a.velocity_kph) || 0)))} />
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (<Skeleton key={i} className="h-24 w-full rounded-2xl" />))}
          </div>
        ) : (data?.asteroids || []).length === 0 ? (
          <div className="glass p-10 text-white/70">No near-Earth objects logged for this date.</div>
        ) : (
          <div className="space-y-3 stagger">
            {data.asteroids.map((a) => (
              <div key={a.id} className="glass hover-lift p-5 md:p-6" data-testid={`neo-${a.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-center">
                  <div className="md:col-span-4">
                    <div className="flex items-center gap-3">
                      <div className="display text-lg md:text-xl tracking-tight">{a.name}</div>
                      {a.hazardous ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest rounded-full bg-red-500/15 text-red-300 border border-red-400/30">
                          <AlertTriangle size={11} /> Hazardous
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-400/20">
                          <ShieldCheck size={11} /> Not hazardous
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/45 mt-1">{a.approach_date}</div>
                  </div>
                  <Cell label="Diameter" value={`${fmtNum(a.diameter_min_m, 0)} – ${fmtNum(a.diameter_max_m, 0)} m`} />
                  <Cell label="Velocity" value={`${fmtNum(a.velocity_kph, 0)} km/h`} />
                  <Cell label="Miss distance" value={`${fmtNum(a.miss_distance_km, 0)} km`} sub={`${fmtNum(a.miss_distance_lunar, 1)} LD`} />
                  <div className="md:col-span-2 md:text-right">
                    <a href={a.nasa_jpl_url} target="_blank" rel="noopener noreferrer" className="btn-pill btn-secondary inline-flex !py-2 !text-[12px]">
                      Details <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="glass p-5 min-w-0">
      <div className="eyebrow">{label}</div>
      <div className={`display mt-3 text-xl md:text-2xl lg:text-[26px] tracking-tight truncate ${accent === "hazard" ? "text-red-300" : "text-white"}`} title={String(value)}>{value}</div>
    </div>
  );
}

function Cell({ label, value, sub }) {
  return (
    <div className="md:col-span-2">
      <div className="eyebrow">{label}</div>
      <div className="text-sm md:text-base text-white mt-2">{value}</div>
      {sub && <div className="text-xs text-white/45 mt-1">{sub}</div>}
    </div>
  );
}
